<template>
  <div class="sa-page">
    <div class="sa-box">
      <div class="sa-header">
        <h1 class="sa-title">店家線上預約系統申請</h1>
        <p class="sa-subtitle">填寫基本資料後送出申請，審核通過即可登入店家後台管理預約。</p>
      </div>

      <!-- 送出成功 -->
      <div v-if="submitted" class="sa-success">
        <div class="sa-success__icon">✓</div>
        <h2 class="sa-success__title">申請已送出！</h2>
        <p class="sa-success__msg">
          我們將盡快審核您的資料，審核通過後管理員會將您的專屬登入連結傳送給您。
        </p>
      </div>

      <template v-else>
        <!-- 基本資料 -->
        <section class="sa-section">
          <h2 class="sa-section-title">店家資料</h2>

          <div class="sa-field">
            <label>地區 <span class="sa-req">*</span></label>
            <select v-model="form.region_id" class="sa-input" :class="{ 'sa-input--err': errors.region_id }">
              <option value="">請選擇地區</option>
              <option v-for="r in regions" :key="r.id" :value="r.id">{{ r.name }}</option>
            </select>
            <p v-if="errors.region_id" class="sa-err">{{ errors.region_id }}</p>
          </div>

          <div class="sa-field">
            <label>店名 <span class="sa-req">*</span></label>
            <input v-model="form.name" class="sa-input" :class="{ 'sa-input--err': errors.name }" placeholder="請輸入店名" />
            <p v-if="errors.name" class="sa-err">{{ errors.name }}</p>
          </div>

          <div class="sa-field">
            <label>地址 <span class="sa-req">*</span></label>
            <input v-model="form.address" class="sa-input" :class="{ 'sa-input--err': errors.address }" placeholder="請輸入完整地址" />
            <p v-if="errors.address" class="sa-err">{{ errors.address }}</p>
          </div>

          <div class="sa-field-row">
            <div class="sa-field">
              <label>電話 <span class="sa-req">*</span></label>
              <input v-model="form.phone" class="sa-input" :class="{ 'sa-input--err': errors.phone }" placeholder="例：07-1234567" />
              <p v-if="errors.phone" class="sa-err">{{ errors.phone }}</p>
            </div>
            <div class="sa-field">
              <label>通知 Email <span class="sa-req">*</span></label>
              <input v-model="form.email" type="email" class="sa-input" :class="{ 'sa-input--err': errors.email }" placeholder="接收預約通知" />
              <p v-if="errors.email" class="sa-err">{{ errors.email }}</p>
            </div>
          </div>

          <div class="sa-field">
            <label>LINE ID <span class="sa-hint">（選填，讓顧客可直接加 LINE 聯絡）</span></label>
            <input v-model="form.line_id" class="sa-input" placeholder="例：@jiliskin 或 jiliskin" />
          </div>

          <div class="sa-field">
            <label>服務分類</label>
            <div class="sa-checkboxes">
              <label v-for="c in categories" :key="c.id" class="sa-checkbox">
                <input type="checkbox" :value="c.id" v-model="form.category_ids" />
                <span>{{ c.name }}</span>
              </label>
            </div>
          </div>

          <div class="sa-field">
            <label>封面圖片 <span class="sa-req">*</span></label>
            <div class="sa-img-upload" :class="{ 'sa-img-upload--err': errors.cover_image }">
              <img v-if="coverPreview" :src="coverPreview" class="sa-img-preview" />
              <label class="sa-img-btn">
                <input type="file" accept="image/*" style="display:none" @change="onImageChange" />
                {{ coverPreview ? '🔄 重新選擇' : '📷 選擇封面圖片' }}
              </label>
            </div>
            <p class="sa-hint-text">建議比例 1:1，JPG / PNG，最大 8MB</p>
            <p v-if="errors.cover_image" class="sa-err">{{ errors.cover_image }}</p>
          </div>

          <div class="sa-field">
            <label>店家簡介</label>
            <textarea v-model="form.description" class="sa-input sa-textarea" rows="3" placeholder="選填，簡短描述您的服務特色" />
          </div>
        </section>

        <!-- 後台帳號 -->
        <section class="sa-section">
          <h2 class="sa-section-title">後台登入帳號</h2>
          <p class="sa-section-hint">審核通過後，使用此帳號登入店家後台管理預約。</p>

          <div class="sa-field-row">
            <div class="sa-field">
              <label>帳號 <span class="sa-req">*</span></label>
              <input
                v-model="form.account"
                class="sa-input"
                :class="{ 'sa-input--err': errors.account }"
                placeholder="英數字，建議 6～20 碼"
                autocomplete="off"
              />
              <p v-if="errors.account" class="sa-err">{{ errors.account }}</p>
            </div>
            <div class="sa-field">
              <label>密碼 <span class="sa-req">*</span></label>
              <input
                v-model="form.password"
                type="password"
                class="sa-input"
                :class="{ 'sa-input--err': errors.password }"
                placeholder="至少 6 碼"
                autocomplete="new-password"
              />
              <p v-if="errors.password" class="sa-err">{{ errors.password }}</p>
            </div>
          </div>
        </section>

        <p v-if="apiError" class="sa-api-error">{{ apiError }}</p>

        <!-- 服務聲明勾選 -->
        <div class="sa-agree">
          <label class="sa-agree__label">
            <input type="checkbox" v-model="agreed" class="sa-agree__check" />
            <span>
              我已閱讀並同意：本平台（JILI）僅提供線上預約媒合服務，所有服務由店家獨立提供，平台不介入任何消費糾紛或賠償責任。平台保留對違規店家下架或停權之權利。
            </span>
          </label>
          <p v-if="errors.agreed" class="sa-err">{{ errors.agreed }}</p>
        </div>

        <div class="sa-footer">
          <button class="sa-btn" :disabled="submitting" @click="submit">
            <span v-if="submitting" class="sa-spinner" />
            {{ submitting ? '送出中…' : '送出申請' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import axios from 'axios'

const regions    = ref([])
const categories = ref([])
const submitted  = ref(false)
const submitting = ref(false)
const apiError   = ref('')
const errors     = ref({})
const agreed     = ref(false)

const coverFile    = ref(null)
const coverPreview = ref('')

function onImageChange(e) {
  const file = e.target.files[0]
  if (!file) return
  const allowed = ['image/jpeg', 'image/png', 'image/webp']
  if (!allowed.includes(file.type)) {
    errors.value.cover_image = '圖片格式不支援，請上傳 JPG、PNG 或 WEBP'
    return
  }
  if (file.size > 8 * 1024 * 1024) {
    errors.value.cover_image = '圖片檔案太大，請上傳 8MB 以內的圖片'
    return
  }
  coverFile.value = file
  coverPreview.value = URL.createObjectURL(file)
  if (errors.value.cover_image) errors.value.cover_image = ''
}

const form = ref({
  region_id:    '',
  name:         '',
  address:      '',
  phone:        '',
  email:        '',
  line_id:      '',
  description:  '',
  category_ids: [],
  account:      '',
  password:     '',
})

async function loadOptions() {
  try {
    const [rRes, cRes] = await Promise.all([
      axios.get('/api/booking/regions'),
      axios.get('/api/booking/categories'),
    ])
    regions.value    = rRes.data.regions    || []
    categories.value = cRes.data.categories || []
  } catch {
    // 靜默，表單仍可填寫
  }
}

function validate() {
  const e = {}
  if (!form.value.region_id)                                      e.region_id    = '請選擇地區'
  if (!form.value.name.trim())                                    e.name         = '請輸入店名'
  if (!form.value.address.trim())                                 e.address      = '請輸入地址'
  else if (form.value.address.trim().length < 8)                 e.address      = '地址太短，請輸入完整地址'
  else if (!/\d/.test(form.value.address))                       e.address      = '地址須包含門牌號碼'
  else if (!/[路街道巷弄]/.test(form.value.address))             e.address      = '地址須包含路名（例：中山路、信義街）'
  else if (!form.value.address.includes('號'))                   e.address      = '地址須包含完整門牌（例：10號）'
  if (!form.value.phone.trim())                                   e.phone        = '請輸入電話'
  else if (!/^\d{9,10}$/.test(form.value.phone.replace(/[-\s]/g, ''))) e.phone  = '電話格式不正確，請輸入 9～10 碼數字'
  if (!form.value.email.trim())                                   e.email        = '請輸入 Email'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) e.email        = 'Email 格式不正確'
  if (!coverFile.value)                                           e.cover_image  = '請上傳封面圖片'
  if (!form.value.account.trim())                                 e.account      = '請填寫帳號'
  if (form.value.password.length < 6)                            e.password     = '密碼至少 6 碼'
  if (!agreed.value)                                             e.agreed       = '請先閱讀並勾選同意服務聲明'
  errors.value = e
  return Object.keys(e).length === 0
}

async function scrollToFirstError() {
  await nextTick()
  const el = document.querySelector('.sa-err, .sa-input--err, .sa-img-upload--err')
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

async function submit() {
  if (!validate()) { scrollToFirstError(); return }
  submitting.value = true
  apiError.value   = ''
  try {
    const fd = new FormData()
    fd.append('region_id',    Number(form.value.region_id))
    fd.append('name',         form.value.name.trim())
    fd.append('address',      form.value.address.trim())
    fd.append('phone',        form.value.phone.trim())
    fd.append('email',        form.value.email.trim())
    fd.append('line_id',      form.value.line_id.trim() || '')
    fd.append('description',  form.value.description.trim() || '')
    fd.append('account',      form.value.account.trim())
    fd.append('password',     form.value.password)
    fd.append('cover_image',  coverFile.value)
    form.value.category_ids.forEach(id => fd.append('category_ids[]', id))

    await axios.post('/api/booking/store-apply', fd)
    submitted.value = true
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (e) {
    apiError.value = e.response?.data?.message || '送出失敗，請稍後再試'
    await nextTick()
    document.querySelector('.sa-api-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  } finally {
    submitting.value = false
  }
}

onMounted(loadOptions)
</script>

<style scoped>
.sa-page {
  min-height: 100vh;
  background: #f5f7fa;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px 60px;
}

.sa-box {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
  width: 100%;
  max-width: 600px;
  padding: 36px 32px;
}

.sa-header { margin-bottom: 28px; }
.sa-title  { font-size: 1.5rem; font-weight: 700; color: #2c3e50; margin: 0 0 8px; }
.sa-subtitle { font-size: 0.9rem; color: #7f8c8d; margin: 0; }

/* 送出成功 */
.sa-success {
  text-align: center;
  padding: 20px 0 10px;
}
.sa-success__icon {
  width: 56px; height: 56px; border-radius: 50%;
  background: #eafaf1; color: #27ae60;
  font-size: 1.6rem; line-height: 56px;
  margin: 0 auto 16px;
}
.sa-success__title { font-size: 1.2rem; font-weight: 700; color: #2c3e50; margin: 0 0 10px; }
.sa-success__msg   { font-size: 0.92rem; color: #555; margin: 0; line-height: 1.6; }
.sa-link { color: #2980b9; }

/* Section */
.sa-section { margin-bottom: 28px; }
.sa-section-title {
  font-size: 1rem; font-weight: 700; color: #2c3e50;
  margin: 0 0 4px; padding-bottom: 8px;
  border-bottom: 1px solid #e8ecf0;
}
.sa-section-hint { font-size: 0.84rem; color: #7f8c8d; margin: 6px 0 16px; }

/* Fields */
.sa-field { margin-top: 16px; }
.sa-field label { display: block; font-size: 0.87rem; font-weight: 600; color: #444; margin-bottom: 5px; }
.sa-req  { color: #e74c3c; }
.sa-hint { font-size: 0.8rem; color: #888; font-weight: 400; }
.sa-hint-text { font-size: 0.8rem; color: #888; margin: 4px 0 0; }

/* 圖片上傳 */
.sa-img-upload { border: 2px dashed #ccc; border-radius: 8px; padding: 16px; text-align: center; }
.sa-img-upload--err { border-color: #e74c3c; }
.sa-img-preview { max-width: 100%; max-height: 180px; border-radius: 6px; margin-bottom: 10px; display: block; object-fit: cover; }
.sa-img-btn { display: inline-block; background: #8b6f5c; color: #fff; padding: 8px 18px; border-radius: 6px; cursor: pointer; font-size: 0.88rem; transition: background 0.15s; }
.sa-img-btn:hover { background: #6d5347; }

.sa-field-row { display: flex; gap: 14px; }
.sa-field-row .sa-field { flex: 1; }

.sa-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #dce1e7;
  border-radius: 8px;
  font-size: 0.92rem;
  color: #2c3e50;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.sa-input:focus { outline: none; border-color: #2c3e50; box-shadow: 0 0 0 3px rgba(44,62,80,0.07); }
.sa-input--err { border-color: #e74c3c; }
.sa-textarea { resize: vertical; }

/* Checkboxes */
.sa-checkboxes { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 6px; }
.sa-checkbox {
  display: flex; align-items: center; gap: 5px;
  font-size: 0.87rem; cursor: pointer; color: #444;
}

/* Errors */
.sa-err { font-size: 0.8rem; color: #e74c3c; margin: 4px 0 0; }
.sa-api-error {
  background: #fdedec; border: 1px solid #f5b7b1; border-radius: 8px;
  padding: 10px 14px; font-size: 0.87rem; color: #c0392b; margin: 0 0 16px;
}

/* 服務聲明勾選 */
.sa-agree {
  margin: 20px 0 8px;
  background: #f9f6f3;
  border: 1px solid #e8ddd7;
  border-radius: 8px;
  padding: 14px 16px;
}
.sa-agree__label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 0.88rem;
  color: #555;
  line-height: 1.6;
}
.sa-agree__check {
  margin-top: 3px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  accent-color: #8b6f5c;
  cursor: pointer;
}

/* Footer */
.sa-footer { display: flex; justify-content: flex-end; margin-top: 8px; }

.sa-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 28px; border-radius: 8px; border: none;
  background: #2c3e50; color: #fff;
  font-size: 0.95rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s;
}
.sa-btn:hover:not(:disabled) { background: #34495e; }
.sa-btn:disabled { opacity: 0.55; cursor: not-allowed; }

/* Spinner */
.sa-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 520px) {
  .sa-box { padding: 24px 16px; }
  .sa-field-row { flex-direction: column; gap: 0; }
}
</style>
